import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EnvironmentPropDTO } from "../../dto/EnvironmentPropDTO";
import { PropDetailsDTO } from "../../dto/PropDetailsDTO";
import { RootState } from "../Store";

export type EnvironmentPropLoadStatus = "idle" | "loading" | "loaded" | "failed";
export type EnvironmentPropRenderStatus = "loading" | "loaded" | "failed";

export interface EnvironmentPropState {
  environmentId: string;
  status: EnvironmentPropLoadStatus;
  error?: string;
  placements: EnvironmentPropDTO[];
  detailsByPropId: Record<string, PropDetailsDTO>;
  renderUrisByPropId: Record<string, string>;
  renderStatusByPropId: Record<string, EnvironmentPropRenderStatus>;
}

const initialState: EnvironmentPropState = {
  environmentId: "",
  status: "idle",
  placements: [],
  detailsByPropId: {},
  renderUrisByPropId: {},
  renderStatusByPropId: {},
};

export const environmentPropSlice = createSlice({
  name: "environmentProps",
  initialState,
  reducers: {
    resetEnvironmentProps: () => initialState,
    loadEnvironmentPropsStart: (
      state,
      action: PayloadAction<{ environmentId: string }>
    ) => {
      state.environmentId = action.payload.environmentId;
      state.status = "loading";
      state.error = undefined;
      state.placements = [];
      state.detailsByPropId = {};
      state.renderUrisByPropId = {};
      state.renderStatusByPropId = {};
    },
    loadEnvironmentPropsSuccess: (
      state,
      action: PayloadAction<{
        environmentId: string;
        placements: EnvironmentPropDTO[];
        details: PropDetailsDTO[];
      }>
    ) => {
      state.environmentId = action.payload.environmentId;
      state.status = "loaded";
      state.error = undefined;
      state.placements = action.payload.placements;
      state.detailsByPropId = {};
      action.payload.details.forEach((detail) => {
        state.detailsByPropId[detail.id] = detail;
      });
    },
    syncEnvironmentPropPlacements: (
      state,
      action: PayloadAction<{
        environmentId: string;
        placements: EnvironmentPropDTO[];
      }>
    ) => {
      const visiblePropIds = new Set(
        action.payload.placements.map((placement) => placement.propId)
      );

      state.environmentId = action.payload.environmentId;
      state.status = "loaded";
      state.error = undefined;
      state.placements = action.payload.placements;
      state.detailsByPropId = Object.entries(state.detailsByPropId).reduce<
        Record<string, PropDetailsDTO>
      >((result, [propId, detail]) => {
        if (visiblePropIds.has(propId)) result[propId] = detail;
        return result;
      }, {});
      state.renderUrisByPropId = Object.entries(state.renderUrisByPropId).reduce<
        Record<string, string>
      >((result, [propId, renderUri]) => {
        if (visiblePropIds.has(propId)) result[propId] = renderUri;
        return result;
      }, {});
      state.renderStatusByPropId = Object.entries(
        state.renderStatusByPropId
      ).reduce<Record<string, EnvironmentPropRenderStatus>>(
        (result, [propId, renderStatus]) => {
          if (visiblePropIds.has(propId)) result[propId] = renderStatus;
          return result;
        },
        {}
      );
    },
    upsertEnvironmentPropDetails: (
      state,
      action: PayloadAction<{ details: PropDetailsDTO[] }>
    ) => {
      action.payload.details.forEach((detail) => {
        state.detailsByPropId[detail.id] = detail;
      });
    },
    loadEnvironmentPropsFailure: (
      state,
      action: PayloadAction<{ environmentId: string; error: string }>
    ) => {
      state.environmentId = action.payload.environmentId;
      state.status = "failed";
      state.error = action.payload.error;
      state.placements = [];
      state.detailsByPropId = {};
      state.renderUrisByPropId = {};
      state.renderStatusByPropId = {};
    },
    loadEnvironmentPropRenderStart: (
      state,
      action: PayloadAction<{ propId: string }>
    ) => {
      state.renderStatusByPropId[action.payload.propId] = "loading";
    },
    loadEnvironmentPropRenderSuccess: (
      state,
      action: PayloadAction<{ propId: string; downloadUri: string }>
    ) => {
      state.renderStatusByPropId[action.payload.propId] = "loaded";
      state.renderUrisByPropId[action.payload.propId] = action.payload.downloadUri;
    },
    loadEnvironmentPropRenderFailure: (
      state,
      action: PayloadAction<{ propId: string }>
    ) => {
      state.renderStatusByPropId[action.payload.propId] = "failed";
    },
  },
});

export const {
  resetEnvironmentProps,
  loadEnvironmentPropsStart,
  loadEnvironmentPropsSuccess,
  syncEnvironmentPropPlacements,
  upsertEnvironmentPropDetails,
  loadEnvironmentPropsFailure,
  loadEnvironmentPropRenderStart,
  loadEnvironmentPropRenderSuccess,
  loadEnvironmentPropRenderFailure,
} = environmentPropSlice.actions;

export const selectEnvironmentPropsStatus = (state: RootState) =>
  state.environmentProps.status;
export const selectEnvironmentPropsEnvironmentId = (state: RootState) =>
  state.environmentProps.environmentId;
export const selectEnvironmentPropPlacements = (state: RootState) =>
  state.environmentProps.placements;
export const selectEnvironmentPropDetailsByPropId = (state: RootState) =>
  state.environmentProps.detailsByPropId;
export const selectEnvironmentPropRenderUrisByPropId = (state: RootState) =>
  state.environmentProps.renderUrisByPropId;
export const selectEnvironmentPropRenderStatusByPropId = (state: RootState) =>
  state.environmentProps.renderStatusByPropId;

export default environmentPropSlice.reducer;
